import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, 
  Grid, 
  MapPin,
  Heart,
  RefreshCw,
  Loader2,
  Satellite,
  Ruler,
  Navigation
} from 'lucide-react';
import { useMap } from '../contexts/MapContext';
import { useAuth } from '../hooks/useAuth';
import { useMonuments } from '../hooks/useMonuments';
import { monumentAPI } from '../services/api';
import axios from 'axios';
import toast from 'react-hot-toast';

const Discover = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedEra, setSelectedEra] = useState('');
  const [eras, setEras] = useState([]);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'grid'
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [measureControl, setMeasureControl] = useState(null);
  const [locateControl, setLocateControl] = useState(null);
  const [tileLayer, setTileLayer] = useState(null);
  const [mapType, setMapType] = useState('street'); // 'street' or 'satellite'
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  
  const { 
    bounds, 
    center, 
    zoom, 
    loading, 
    updateBounds, 
    updateCenter, 
    updateZoom, 
    setLoading, 
    restoreLastViewed 
  } = useMap();
  const { isAuthenticated } = useAuth();
  
  const [monuments, setMonuments] = useState([]);

  // Set local eras data on component mount (no backend dependency)
  useEffect(() => {
    const localEras = [
      'Ancient',
      'Classical', 
      'Medieval',
      'Early Modern',
      'Modern',
      'Contemporary'
    ];
    setEras(localEras);
  }, []);

  // Search and fetch monuments
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery || selectedEra) {
        fetchMonuments();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedEra]);

  // Initialize map when view mode changes to map
  useEffect(() => {
    if (viewMode === 'map' && mapRef.current && !map) {
      initializeMap();
    }
    
    // Cleanup map when switching away from map view
    return () => {
      if (map && viewMode !== 'map') {
        map.remove();
        setMap(null);
      }
    };
  }, [viewMode]);

  // Update map when monuments change
  useEffect(() => {
    if (map && monuments.length > 0) {
      updateMapMarkers(monuments);
    }
  }, [map, monuments]);

  // Cleanup map on component unmount
  useEffect(() => {
    return () => {
      if (map) {
        map.remove();
        setMap(null);
      }
    };
  }, []);

  // Fetch monuments in bounds
  useEffect(() => {
    if (bounds && viewMode === 'map') {
      fetchMonumentsInBounds();
    }
  }, [bounds, viewMode]);

  // Map initialization
  const initializeMap = () => {
    if (!mapRef.current || !window.L || map) return;

    const L = window.L;
    
    // Clear any existing map instance
    if (mapRef.current._leaflet_id) {
      return;
    }
    
    // Initialize map with default coordinates
    const mapInstance = L.map(mapRef.current).setView([center.lat || 40.7128, center.lng || -74.0060], zoom || 13);

    // Create tile layers for different map types
    const streetLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    });

    const satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: '© Esri & Contributors',
      maxZoom: 19,
    });

    // Add default tile layer
    streetLayer.addTo(mapInstance);
    setTileLayer(streetLayer);

    // Add layer control
    const baseLayers = {
      "Street View": streetLayer,
      "Satellite View": satelliteLayer
    };

    L.control.layers(baseLayers).addTo(mapInstance);

    // Note: Measure control removed due to plugin loading issues
    // Can be added later with proper plugin setup

    // Add locate control for current location
    const locateControl = L.control.locate({
      position: 'topleft',
      drawCircle: true,
      follow: true,
      setView: true,
      keepCurrentZoomLevel: true,
      markerStyle: {
        weight: 2,
        opacity: 1,
        fillOpacity: 0.8
      },
      circleStyle: {
        weight: 2,
        opacity: 0.3,
        fillOpacity: 0.1
      },
      icon: 'fa fa-crosshairs',
      metric: true,
      strings: {
        title: "My location",
        popup: "You are within {distance} {unit} from this point",
        outsideMapBoundsMsg: "You seem located outside the boundaries of the map"
      }
    });
    locateControl.addTo(mapInstance);
    setLocateControl(locateControl);

    // Handle map events
    mapInstance.on('moveend', () => {
      const newBounds = mapInstance.getBounds();
      updateBounds({
        north: newBounds.getNorth(),
        south: newBounds.getSouth(),
        east: newBounds.getEast(),
        west: newBounds.getWest(),
      });
      updateCenter(mapInstance.getCenter());
      updateZoom(mapInstance.getZoom());
    });

    // Handle layer switch
    mapInstance.on('baselayerchange', (e) => {
      setMapType(e.name === 'Satellite View' ? 'satellite' : 'street');
      setTileLayer(e.layer);
    });

    setMap(mapInstance);
  };

  // Fetch monuments
  const fetchMonuments = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (selectedEra) filters.era = selectedEra;
      
      const results = await monumentAPI.searchMonuments(searchQuery || 'historical monuments', filters);
      setMonuments(results || []);
    } catch (error) {
      console.error('Failed to fetch monuments:', error);
      toast.error('Failed to load monuments');
      setMonuments([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Fetch monuments in bounds
  const fetchMonumentsInBounds = async () => {
    if (!bounds) return;
    
    setLoading(true);
    try {
      const boundsObj = {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      };
      
      const results = await monumentAPI.getMonumentsInBounds(boundsObj);
      setMonuments(results || []);
    } catch (error) {
      console.error('Failed to fetch monuments in bounds:', error);
      toast.error('Failed to load monuments');
      setMonuments([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Update map markers
  const updateMapMarkers = (monuments) => {
    if (!map || !window.L) return;

    const L = window.L;
    
    // Clear existing markers
    markersRef.current.forEach(marker => {
      map.removeLayer(marker);
    });
    markersRef.current = [];

    // Add new markers with enhanced styling
    monuments.forEach((monument) => {
      if (!monument.coordinates || monument.coordinates.length < 2) return;

      const markerColor = mapType === 'satellite' ? '#FFD700' : '#2563EB';
      
      const customIcon = L.divIcon({
        html: `<div class="flex items-center justify-center w-10 h-10 rounded-full shadow-lg hover:scale-110 transition-transform cursor-pointer" style="background-color: ${markerColor}; border: 3px solid white;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        </div>`,
        className: 'custom-marker',
        iconSize: [40, 40],
        iconAnchor: [20, 40],
      });

      const marker = L.marker([monument.coordinates[1], monument.coordinates[0]], {
        icon: customIcon,
      });

      // Create enhanced popup content
      const popupContent = `
        <div class="p-4 min-w-[250px] max-w-[300px]">
          <h3 class="font-serif font-bold text-lg mb-2">${monument.title}</h3>
          ${monument.mainImage ? `<img src="${monument.mainImage}" alt="${monument.title}" class="w-full h-40 object-cover rounded mb-3">` : ''}
          <div class="mb-3">
            <span class="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">${monument.era || 'Unknown Era'}</span>
          </div>
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center space-x-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-red-500">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06 1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06a5.5 5.5 0 0 0-7.78z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <span class="text-sm font-medium">${monument.likes || 0} likes</span>
            </div>
            <div class="text-xs text-gray-500">
              ${monument.coordinates ? `${monument.coordinates[1].toFixed(4)}°, ${monument.coordinates[0].toFixed(4)}°` : ''}
            </div>
          </div>
          <a href="/story/${monument.slug}" class="block w-full bg-blue-600 text-white text-center py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium">
            Read Full Story
          </a>
        </div>
      `;

      marker.bindPopup(popupContent, {
        maxWidth: 300,
        className: 'custom-popup'
      });

      // Add hover effect
      marker.on('mouseover', function() {
        this.openPopup();
      });

      marker.addTo(map);
      markersRef.current.push(marker);
    });

    setMarkers(markersRef.current);
  };

  const handleLike = async (monumentId) => {
    if (!isAuthenticated) {
      toast.error('Please login to like monuments');
      return;
    }

    // For now, just show a message since likes require backend integration
    toast.success('Like feature coming soon!');
    console.log('Like monument:', monumentId);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h1 className="text-2xl font-serif font-bold text-gray-900">
              Discover Historical Monuments
            </h1>
            
            <div className="flex items-center space-x-4 flex-wrap">
              {/* Search and Filters */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search monuments..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 w-full sm:w-64"
                />
              </div>

              <select
                value={selectedEra}
                onChange={(e) => setSelectedEra(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                <option value="">All Eras</option>
                {eras.map((era) => (
                  <option key={era} value={era}>
                    {era}
                  </option>
                ))}
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('map')}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  viewMode === 'map'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <MapPin className="w-5 h-5" />
                <span className="ml-1">Map</span>
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <Grid className="w-5 h-5" />
                <span className="ml-1">Grid</span>
              </button>
            </div>

            {/* Map Controls Info */}
            {viewMode === 'map' && (
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <Satellite className="w-4 h-4" />
                  <span>Satellite</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Ruler className="w-4 h-4" />
                  <span>Measure</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Navigation className="w-4 h-4" />
                  <span>Location</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/80">
            <Loader2 className="animate-spin text-blue-600" />
          </div>
        )}

        {viewMode === 'map' ? (
          /* Map View */
          <div className="h-[calc(100vh-8rem)] relative z-10" ref={mapRef}></div>
        ) : (
          /* Grid View */
          <div className="max-w-7xl mx-auto px-4 py-8">
            {monuments.length === 0 ? (
              <div className="text-center py-20">
                <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-serif font-semibold text-gray-900 mb-2">
                  No monuments found
                </h3>
                <p className="text-gray-600">
                  {searchQuery || selectedEra 
                    ? 'Try adjusting your search or filters' 
                    : 'Try searching for historical monuments'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {monuments.map((monument, index) => (
                  <motion.div
                    key={monument._id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                    className="group cursor-pointer"
                  >
                    <Link to={`/story/${monument.slug}`}>
                      <div className="bg-white rounded-lg shadow-lg overflow-hidden h-full hover:shadow-xl transition-shadow">
                        <div className="relative h-48 overflow-hidden">
                          {monument.mainImage ? (
                            <img
                              src={monument.mainImage}
                              alt={monument.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                              <MapPin className="w-12 h-12 text-blue-600" />
                            </div>
                          )}
                          <div className="absolute top-2 right-2 bg-white/90 px-2 py-1 rounded-full text-xs text-gray-900">
                            {monument.era || 'Unknown Era'}
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="text-lg font-serif font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                            {monument.title}
                          </h3>
                          <div className="flex items-center justify-between text-gray-600">
                            <div className="flex items-center space-x-1">
                              <Heart className="w-4 h-4" />
                              <span className="text-sm">{monument.likes || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Discover;
